import { useState } from 'react';

// ============================================
// Demo Booking Widget - Interactive Demo
// Matches widget.js exactly for visual parity
// ============================================

type DemoStep = 'dates' | 'calendar' | 'details' | 'payment' | 'confirmed';

interface DemoState {
  checkIn: string | null;
  checkOut: string | null;
  guests: number;
  name: string;
  email: string;
  phone: string;
}

const NIGHTLY_RATE = 120;
const PROPERTY_NAME = 'Oceanfront Studio Apartment';
const PROPERTY_LOCATION = 'Lisbon, Portugal';
const MAX_GUESTS = 6;

// ============================================
// Calendar Helper Functions
// ============================================

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Format date for API (YYYY-MM-DD)
 */
const formatDateAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date for display (e.g., "Jan 15")
 */
const formatDateDisplay = (dateStr: string | null): string => {
  if (!dateStr) return 'Add date';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Check if date is in the past
 */
const isPastDate = (dateStr: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateStr + 'T00:00:00');
  return date < today;
};

/**
 * Get days in month
 */
const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get first day of month (0 = Sunday)
 */
const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

/**
 * Calculate nights between two dates
 */
const calculateNights = (checkIn: string | null, checkOut: string | null): number => {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

/**
 * Simple email validation
 */
const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

interface DemoBookingWidgetProps {
  onJoinWaitlist?: () => void;
  /** Override primary color (default: neutral-900 / #171717) */
  primaryColor?: string;
  /** Override border radius in px (default: 8) */
  borderRadius?: number;
  /** Override property name */
  propertyName?: string;
  /** Override property location */
  propertyLocation?: string;
  /** Override nightly rate */
  nightlyRate?: number;
  /** Override max guests */
  maxGuests?: number;
}

/**
 * Interactive Demo Booking Widget
 * Matches widget.js exactly for visual parity (view switching approach)
 * Accepts optional theme props for live preview in embed customization.
 */
const DemoBookingWidget = ({
  onJoinWaitlist,
  primaryColor,
  borderRadius,
  propertyName,
  propertyLocation,
  nightlyRate,
  maxGuests,
}: DemoBookingWidgetProps) => {
  // Theme: use props or defaults
  const pc = primaryColor || '#171717';
  const br = borderRadius ?? 8;
  const pName = propertyName || PROPERTY_NAME;
  const pLocation = propertyLocation || PROPERTY_LOCATION;
  const rate = nightlyRate ?? NIGHTLY_RATE;
  const guestsMax = maxGuests ?? MAX_GUESTS;

  // Style helpers for themed elements
  const btnStyle: React.CSSProperties = { backgroundColor: pc, borderRadius: br };
  const textPrimary: React.CSSProperties = primaryColor ? { color: pc } : {};
  const radiusLg: React.CSSProperties = borderRadius !== undefined ? { borderRadius: br } : {};
  const [step, setStep] = useState<DemoStep>('dates');
  const [isLoading, setIsLoading] = useState(false);

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  // Form state
  const [formData, setFormData] = useState<DemoState>({
    checkIn: null,
    checkOut: null,
    guests: 2,
    name: '',
    email: '',
    phone: '',
  });

  const nights = calculateNights(formData.checkIn, formData.checkOut);
  const total = nights * rate;

  const updateField = <K extends keyof DemoState>(field: K, value: DemoState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Date selection logic (matches widget.js)
  const selectDate = (dateStr: string) => {
    if (isPastDate(dateStr)) return;

    setFormData(prev => {
      if (!prev.checkIn || (prev.checkIn && prev.checkOut)) {
        // First click or reset: set check-in
        return { ...prev, checkIn: dateStr, checkOut: null };
      } else if (dateStr > prev.checkIn) {
        // Second click after check-in: set check-out
        return { ...prev, checkOut: dateStr };
      } else {
        // Clicked before check-in: reset to new check-in
        return { ...prev, checkIn: dateStr, checkOut: null };
      }
    });
  };

  const isDateSelected = (dateStr: string): boolean => {
    return dateStr === formData.checkIn || dateStr === formData.checkOut;
  };

  const isInRange = (dateStr: string): boolean => {
    if (!formData.checkIn || !formData.checkOut) return false;
    return dateStr > formData.checkIn && dateStr < formData.checkOut;
  };

  const canReserve = nights > 0;
  const canContinueToPayment = formData.name.trim().length >= 2 && isValidEmail(formData.email);

  const handleCompletePayment = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('confirmed');
    }, 1500);
  };

  const resetDemo = () => {
    setStep('dates');
    setFormData({
      checkIn: null,
      checkOut: null,
      guests: 2,
      name: '',
      email: '',
      phone: '',
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (calendarMonth === 0) {
        setCalendarMonth(11);
        setCalendarYear(calendarYear - 1);
      } else {
        setCalendarMonth(calendarMonth - 1);
      }
    } else {
      if (calendarMonth === 11) {
        setCalendarMonth(0);
        setCalendarYear(calendarYear + 1);
      } else {
        setCalendarMonth(calendarMonth + 1);
      }
    }
  };

  // Generate calendar days
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
    const firstDay = getFirstDayOfMonth(calendarYear, calendarMonth);
    const days: JSX.Element[] = [];

    // Empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateAPI(new Date(calendarYear, calendarMonth, day));
      const isPast = isPastDate(dateStr);
      const isSelected = isDateSelected(dateStr);
      const inRange = isInRange(dateStr);
      const isCheckIn = dateStr === formData.checkIn;
      const isCheckOut = dateStr === formData.checkOut;

      let dayClasses = 'aspect-square flex flex-col items-center justify-center cursor-pointer rounded-lg transition-colors min-h-[44px]';

      if (isPast) {
        dayClasses += ' opacity-35 cursor-not-allowed';
      } else if (isSelected) {
        dayClasses += primaryColor ? ' text-white' : ' bg-neutral-900 text-white';
      } else if (inRange) {
        dayClasses += ' bg-neutral-100';
      } else {
        dayClasses += ' hover:bg-neutral-100';
      }

      // Adjust border radius for range
      if (isCheckIn && formData.checkOut) {
        dayClasses = dayClasses.replace('rounded-lg', 'rounded-l-lg rounded-r-none');
      } else if (isCheckOut) {
        dayClasses = dayClasses.replace('rounded-lg', 'rounded-r-lg rounded-l-none');
      } else if (inRange) {
        dayClasses = dayClasses.replace('rounded-lg', 'rounded-none');
      }

      const dayStyle: React.CSSProperties = {
        ...(isSelected && primaryColor ? { backgroundColor: pc } : {}),
        ...(borderRadius !== undefined && !isCheckIn && !isCheckOut && !inRange ? { borderRadius: br } : {}),
      };

      days.push(
        <div
          key={day}
          className={dayClasses}
          style={dayStyle}
          onClick={() => !isPast && selectDate(dateStr)}
        >
          <span className={`text-sm font-medium ${isSelected ? 'text-white' : ''}`} style={!isSelected ? textPrimary : {}}>
            {day}
          </span>
          {!isPast && (
            <span className={`text-[10px] mt-0.5 ${isSelected ? 'text-white/80' : 'text-neutral-500'}`}>
              €{rate}
            </span>
          )}
        </div>
      );
    }

    return days;
  };

  // ========================================
  // CALENDAR VIEW (full takeover)
  // ========================================
  if (step === 'calendar') {
    return (
      <div className="border border-neutral-200 bg-white shadow-sm overflow-hidden" style={{ borderRadius: br + 4 }}>
        {/* Calendar header */}
        <div className="flex items-center px-5 py-4 border-b border-neutral-100">
          <button
            type="button"
            aria-label="Back to dates"
            onClick={() => setStep('dates')}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-neutral-100 text-neutral-500 transition-colors mr-3"
            style={{ ['--tw-hover-color' as string]: pc }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-base font-semibold" style={textPrimary}>Select dates</div>
        </div>

        {/* Calendar body */}
        <div className="p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => navigateMonth('prev')}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-neutral-200 text-neutral-500 hover:bg-neutral-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-[15px] font-semibold" style={textPrimary}>
              {MONTH_NAMES[calendarMonth]} {calendarYear}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => navigateMonth('next')}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-neutral-200 text-neutral-500 hover:bg-neutral-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-2">
            {WEEKDAYS.map((day) => (
              <div key={day} className="text-center text-[11px] font-semibold text-neutral-400 uppercase py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {renderCalendarDays()}
          </div>

          {/* Selection summary */}
          <div className="flex gap-4 mt-4 p-3 bg-neutral-50" style={radiusLg}>
            <div className="flex-1">
              <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-0.5">Check-in</div>
              <div className={`text-sm font-medium ${formData.checkIn ? '' : 'text-neutral-500'}`} style={formData.checkIn ? textPrimary : {}}>
                {formatDateDisplay(formData.checkIn)}
              </div>
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-0.5">Check-out</div>
              <div className={`text-sm font-medium ${formData.checkOut ? '' : 'text-neutral-500'}`} style={formData.checkOut ? textPrimary : {}}>
                {formatDateDisplay(formData.checkOut)}
              </div>
            </div>
            {nights > 0 && (
              <div className="flex-1 text-right">
                <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-0.5">Total</div>
                <div className="text-sm font-medium" style={textPrimary}>€{total}</div>
              </div>
            )}
          </div>
        </div>

        {/* Done button */}
        {formData.checkIn && formData.checkOut && (
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={() => setStep('dates')}
              className="w-full text-white text-base font-medium py-3.5 transition-colors"
              style={btnStyle}
            >
              Done
            </button>
          </div>
        )}
      </div>
    );
  }

  // ========================================
  // MAIN WIDGET LAYOUT (non-calendar views)
  // ========================================
  return (
    <div className="border border-neutral-200 bg-white shadow-sm overflow-hidden" style={{ borderRadius: br + 4 }}>
      {/* Property header */}
      <div className="px-5 py-5 border-b border-neutral-100">
        <div className="text-base font-semibold mb-1" style={textPrimary}>
          {pName}
        </div>
        <p className="text-[13px] text-neutral-500">{pLocation}</p>
      </div>

      {/* Price header (when dates selected) */}
      {nights > 0 && step !== 'confirmed' && (
        <div className="px-5 py-4 bg-neutral-50 border-b border-neutral-100">
          <div className="flex justify-between items-baseline">
            <span className="text-[13px] text-neutral-500">Total</span>
            <span>
              <span className="text-lg font-semibold" style={textPrimary}>€{total}</span>
              <span className="text-[13px] text-neutral-500 ml-1">
                for {nights} night{nights > 1 ? 's' : ''}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Body content */}
      <div className="p-5">
        {/* ========== STEP: DATES ========== */}
        {step === 'dates' && (
          <>
            {/* Date fields (clickable) */}
            <div className="grid grid-cols-2 border border-neutral-200 overflow-hidden mb-3" style={radiusLg}>
              <div
                className="px-3.5 py-3 cursor-pointer hover:bg-neutral-50 transition-colors border-r border-neutral-200"
                onClick={() => setStep('calendar')}
              >
                <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Check-in</div>
                <div className={`text-sm ${formData.checkIn ? '' : 'text-neutral-500'}`} style={formData.checkIn ? textPrimary : {}}>
                  {formatDateDisplay(formData.checkIn)}
                </div>
              </div>
              <div
                className="px-3.5 py-3 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => setStep('calendar')}
              >
                <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Check-out</div>
                <div className={`text-sm ${formData.checkOut ? '' : 'text-neutral-500'}`} style={formData.checkOut ? textPrimary : {}}>
                  {formatDateDisplay(formData.checkOut)}
                </div>
              </div>
            </div>

            {/* Guests row */}
            <div className="flex items-center justify-between border border-neutral-200 px-3.5 py-3 mb-4" style={radiusLg}>
              <div>
                <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Guests</div>
                <div className="text-sm" style={textPrimary}>
                  {formData.guests} guest{formData.guests > 1 ? 's' : ''}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Decrease guests"
                  onClick={() => updateField('guests', Math.max(1, formData.guests - 1))}
                  disabled={formData.guests <= 1}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-neutral-200 text-neutral-500 hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="text-base font-medium w-6 text-center" style={textPrimary}>{formData.guests}</span>
                <button
                  type="button"
                  aria-label="Increase guests"
                  onClick={() => updateField('guests', Math.min(guestsMax, formData.guests + 1))}
                  disabled={formData.guests >= guestsMax}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-neutral-200 text-neutral-500 hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Reserve button */}
            <button
              type="button"
              onClick={() => setStep('details')}
              disabled={!canReserve}
              className="w-full text-white text-base font-medium py-3.5 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors"
              style={canReserve ? btnStyle : { borderRadius: br }}
            >
              Reserve
            </button>

            {/* Hint */}
            {!formData.checkIn && !formData.checkOut && (
              <p className="text-xs text-neutral-500 mt-2 text-center">Select dates to see total price</p>
            )}
          </>
        )}

        {/* ========== STEP: DETAILS ========== */}
        {step === 'details' && (
          <>
            {/* Back button */}
            <button
              type="button"
              onClick={() => setStep('dates')}
              className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            {/* Name input */}
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-neutral-600 mb-1.5">Full name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="John Smith"
                className="w-full border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none transition-colors"
                style={{ borderRadius: br }}
              />
            </div>

            {/* Email input */}
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-neutral-600 mb-1.5">Email address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="john@example.com"
                className="w-full border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none transition-colors"
                style={{ borderRadius: br }}
              />
            </div>

            {/* Phone input (optional) */}
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-neutral-600 mb-1.5">Phone (optional)</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="w-full border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none transition-colors"
                style={{ borderRadius: br }}
              />
            </div>

            {/* Continue button */}
            <button
              type="button"
              onClick={() => setStep('payment')}
              disabled={!canContinueToPayment}
              className="w-full text-white text-base font-medium py-3.5 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors"
              style={canContinueToPayment ? btnStyle : { borderRadius: br }}
            >
              Continue to payment
            </button>
          </>
        )}

        {/* ========== STEP: PAYMENT (simplified) ========== */}
        {step === 'payment' && (
          <>
            {/* Back button */}
            <button
              type="button"
              onClick={() => setStep('details')}
              className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            {/* Secure payment header */}
            <div className="flex items-center gap-1.5 text-xs text-neutral-500 mb-4">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Secure payment</span>
            </div>

            {/* Pre-filled card number (read-only visual) */}
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-neutral-600 mb-1.5">Card number</label>
              <div className="w-full border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 bg-neutral-50 font-mono" style={radiusLg}>
                4242 4242 4242 4242
              </div>
            </div>

            {/* Pre-filled expiry and CVC */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[13px] font-medium text-neutral-600 mb-1.5">Expiry</label>
                <div className="w-full border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 bg-neutral-50 font-mono" style={radiusLg}>
                  12/28
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-neutral-600 mb-1.5">CVC</label>
                <div className="w-full border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 bg-neutral-50 font-mono" style={radiusLg}>
                  123
                </div>
              </div>
            </div>

            {/* Pay button */}
            <button
              type="button"
              onClick={handleCompletePayment}
              disabled={isLoading}
              className="w-full text-white text-base font-medium py-3.5 transition-colors"
              style={{ ...btnStyle, opacity: isLoading ? 0.8 : 1 }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                `Pay €${total}`
              )}
            </button>

            {/* Demo notice */}
            <p className="text-xs text-neutral-500 mt-3 text-center">
              This is a demo. Your card will not be charged.
            </p>
          </>
        )}

        {/* ========== STEP: CONFIRMED ========== */}
        {step === 'confirmed' && (
          <div className="text-center py-4">
            {/* Success icon */}
            <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7" style={textPrimary} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div className="text-lg font-semibold mb-2" style={textPrimary}>Booking confirmed</div>
            <p className="text-sm text-neutral-500 mb-5">Confirmation #12345</p>

            {/* Booking summary */}
            <div className="bg-neutral-50 p-4 text-left mb-5" style={radiusLg}>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-500">Check-in</span>
                <span style={textPrimary}>{formatDateDisplay(formData.checkIn)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-500">Check-out</span>
                <span style={textPrimary}>{formatDateDisplay(formData.checkOut)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-500">Guests</span>
                <span style={textPrimary}>{formData.guests}</span>
              </div>
              <div className="flex justify-between text-sm font-medium pt-2 mt-2 border-t border-neutral-200">
                <span style={textPrimary}>Total paid</span>
                <span style={textPrimary}>€{total}</span>
              </div>
            </div>

            {/* CTA */}
            <button
              type="button"
              onClick={() => onJoinWaitlist?.()}
              className="w-full text-white text-base font-medium py-3.5 transition-colors"
              style={btnStyle}
            >
              {onJoinWaitlist ? 'Join the waitlist' : 'Done'}
            </button>

            {/* Make another booking link */}
            <button
              type="button"
              onClick={resetDemo}
              className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors mt-4"
            >
              Make another booking
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoBookingWidget;
